import React from "react";
import { Link } from "../components/L";
import useSEO from "../hooks/useSEO";
import { useLanguage } from "../i18n/LanguageContext";
import { TxHtml as H } from "../components/TxHtml";

const Section = ({ title, children }) => {
  const { tx } = useLanguage();
  return (
    <section className="mb-10">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: "#003F72", fontFamily: "'Noto Sans', sans-serif" }}>
        {tx(title)}
      </h2>
      <div className="text-[#4A5568] leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
};

const List = ({ items, className = "list-disc pl-6 space-y-1" }) => (
  <ul className={className}>
    {items.map((html) => <H key={html} as="li" html={html} />)}
  </ul>
);

const TermsPage = () => {
  const { tx } = useLanguage();
  useSEO({
    title: tx("Kushtet e Përdorimit - Albanian Aligners Academy"),
    description: tx("Termat dhe Kushtet e Përdorimit të platformës Albanian Aligners Academy. Njihuni me rregullat e regjistrimit, pagesës dhe pjesëmarrjes në eventet dhe kurset tona."),
    keywords: "kushtet e perdorimit, termat, terms and conditions, aligners academy, rregullat",
    canonical: "https://aligners.academy/kushtet",
  });

  return (
    <div data-testid="terms-page">
      <section className="relative py-10 md:py-14 px-6" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="terms-hero">
        <div className="max-w-[1350px] mx-auto">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
            <Link to="/" className="hover:text-white text-white/70">{tx("Kryefaqe")}</Link>
            <span className="text-white/70">/</span>
            <span className="text-white">{tx("Kushtet e Përdorimit")}</span>
          </div>
          <h1 className="text-white" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: "42px", lineHeight: 1.2 }} data-testid="terms-title">
            {tx("Kushtet e Përdorimit")}
          </h1>
          <p className="text-white/80 mt-3">{tx("Përditësuar më:")} 24.09.2026</p>
        </div>
      </section>

      {/* tx-catalog-start */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <H className="text-[#4A5568] mb-10 text-[15px] leading-relaxed" html="Mirë se erdhët në <strong>Albanian Aligners Academy</strong> (aligners.academy), platformë e operuar nga <strong>iClear Group SHPK</strong>, me NUIS <strong>M37415002R</strong>, me seli në Rruga Don Bosko, Tiranë, Shqipëri. Duke përdorur këtë faqe, duke u regjistruar në një event, apo duke kryer një pagesë, ju pranoni kushtet e mëposhtme." />

          <Section title="1. Përkufizime">
            <H html="<strong>“Platforma”</strong> — faqja e internetit aligners.academy dhe të gjitha shërbimet e ofruara përmes saj.<br /><strong>“Ne”</strong> — iClear Group SHPK, operatori i platformës.<br /><strong>“Përdoruesi”, “Ju”</strong> — çdo person që viziton faqen ose regjistrohet për një event/kurs.<br /><strong>“Event”</strong> — trajnimet, workshopet, masterclass-et dhe kurset e ofruara nga akademia." />
          </Section>

          <Section title="2. Regjistrimi dhe Llogaria">
            <H html="Për t'u regjistruar në një event, ju kërkohet të plotësoni një formular me të dhëna të sakta (emri, mbiemri, email, telefon, profesioni). Ju jeni përgjegjës për saktësinë e informacionit të dhënë. Regjistrimet me të dhëna të rreme mund të anulohen pa njoftim." />
            <H html="Pjesëmarrja në disa kurse mund të kërkojë kualifikime profesionale specifike (p.sh. dentist, ortodont). Ne rezervojmë të drejtën për të verifikuar kualifikimet tuaja dhe për të refuzuar regjistrimin nëse nuk plotësohen kriteret." />
          </Section>

          <Section title="3. Pagesa">
            <H html="Çmimet e eventeve shfaqen në Euro (EUR) dhe janë të vlefshme sipas kohës së blerjes. Pranojmë këto metoda pagese:" />
            <List items={[
              "<strong>Kartë krediti/debiti</strong> (Visa, Mastercard) — përpunuar në mënyrë të sigurt përmes POK Payments",
              "<strong>Transfertë bankare</strong> — sipas detajeve të dhëna pas regjistrimit",
            ]} />
            <H html="Pagesa përmes kartës është përfundimtare pas konfirmimit nga procesuesi. Ne nuk ruajmë asnjë të dhënë të kartës suaj — të gjitha të dhënat e pagesës përpunohen drejtpërdrejt nga POK Payments në përputhje me standardet PCI DSS." />
            <H html="Në rast se pagesa dështon ose anulohet, regjistrimi juaj mund të mbahet në gjendjen “në pritje” deri në 24 orë; nëse pagesa nuk kryhet, regjistrimi anulohet automatikisht." />
          </Section>

          <Section title="4. Politika e Rimbursimit dhe Anulimit">
            <H html="<strong>Anulim nga pjesëmarrësi:</strong>" />
            <List items={[
              "Më shumë se <strong>14 ditë</strong> para eventit — rimbursim i plotë (100%)",
              "Nga <strong>7 deri 14 ditë</strong> para eventit — rimbursim 50%",
              "Më pak se <strong>7 ditë</strong> para eventit — pa rimbursim, por mund të transferoni vendin tek një koleg",
            ]} />
            <H html={'Për të kërkuar rimbursim, dërgoni një email në <a href="mailto:info@aligners.academy" class="text-[#1CA3B0] font-semibold hover:underline">info@aligners.academy</a> me numrin e transaksionit dhe arsyen. Rimbursimet përpunohen brenda 7 ditësh pune tek metoda origjinale e pagesës.'} />
            <H html="<strong>Anulim nga akademia:</strong> Në raste përjashtimore (mungesë kuorumi, ndryshim orari, forca madhore), rezervojmë të drejtën të anulojmë ose të riplanifikojmë një event. Në këtë rast do të ofrohet ose rimbursim i plotë, ose transferim automatik për në datën e re — sipas preferencës suaj." />
          </Section>

          <Section title="5. Certifikimet dhe Pjesëmarrja">
            <H html="Certifikatat e pjesëmarrjes lëshohen vetëm për ata që marrin pjesë në minimalisht 80% të programit të eventit. Kjo verifikohet përmes listës së pranimit dhe sistemit të badge-eve." />
            <H html="Në rast mungese pa njoftim paraprak, vendi juaj konsiderohet i konsumuar dhe nuk keni të drejtë për rimbursim apo transferim." />
          </Section>

          <Section title="6. Pronësia Intelektuale">
            <H html="I gjithë përmbajtja e platformës — logot, tekstet, fotot, videot, materialet edukative, prezantimet dhe kurikulumet — janë pronë ekskluzive e iClear Group SHPK ose e trajnerëve tanë të autorizuar. Çdo riprodhim, shpërndarje ose përdorim komercial pa autorizim me shkrim është i ndaluar." />
            <H html="Regjistrimet me foto ose video gjatë eventeve mund të përdoren për qëllime promocionale nga akademia, përveç rasteve kur ju e refuzoni shprehimisht me shkrim para eventit." />
          </Section>

          <Section title="7. Sjellja e Pjesëmarrësit">
            <H html="Pjesëmarrësit kërkohet të mbajnë sjellje profesionale dhe të respektojnë trajnerët, stafin dhe kolegët. Rezervojmë të drejtën për të përjashtuar nga eventi çdo person që shfaq sjellje të papërshtatshme, dhunuese ose diskriminuese, pa të drejtë rimbursimi." />
          </Section>

          <Section title="8. Mbrojtja e të Dhënave">
            <H html={'Trajtimi i të dhënave tuaja personale rregullohet nga <a href="/privatesia" class="text-[#1CA3B0] font-semibold hover:underline">Politika jonë e Privatësisë</a>, e cila është pjesë përbërëse e këtyre Kushteve. Ne mbledhim vetëm të dhënat e nevojshme për menaxhimin e regjistrimit tuaj dhe komunikimin rreth eventeve.'} />
          </Section>

          <Section title="9. Kufizimi i Përgjegjësisë">
            <H html="Akademia dhe trajnerët nuk mbajnë përgjegjësi për vendime klinike të marra nga pjesëmarrësit bazuar në informacionin e mësuar në kurse. Të gjitha teknikat e paraqitura janë për qëllime edukative dhe pjesëmarrësi është përgjegjës për aplikimin e drejtë të tyre në praktikë, në përputhje me legjislacionin lokal dhe standardet profesionale." />
          </Section>

          <Section title="10. Ndryshimet në Kushtet">
            <H html="Ne rezervojmë të drejtën për të modifikuar këto Kushte në çdo kohë. Ndryshimet publikohen në këtë faqe me datën e re të përditësimit. Përdorimi i vazhdueshëm i platformës pas ndryshimeve nënkupton pranimin e tyre." />
          </Section>

          <Section title="11. Ligji dhe Juridiksioni">
            <H html="Këto Kushte rregullohen nga legjislacioni i Republikës së Shqipërisë. Çdo mosmarrëveshje që nuk zgjidhet me marrëveshje midis palëve do t'i nënshtrohet juridiksionit ekskluziv të Gjykatave të Tiranës." />
          </Section>

          <Section title="12. Na kontaktoni">
            <H html="Për çdo pyetje rreth këtyre Kushteve:" />
            <List className="list-none space-y-1" items={[
              '<strong>Email:</strong> <a href="mailto:info@aligners.academy" class="text-[#1CA3B0] font-semibold hover:underline">info@aligners.academy</a>',
              '<strong>Telefon:</strong> <a href="tel:+355696056794" class="text-[#1CA3B0] font-semibold hover:underline">+355 696056794</a>',
              "<strong>Adresa:</strong> Rruga Don Bosko, Tiranë, Shqipëri",
              "<strong>NUIS:</strong> M37415002R",
            ]} />
          </Section>
        </div>
      </section>
      {/* tx-catalog-end */}
    </div>
  );
};

export default TermsPage;
